/**
 * Der Abschnitt „Transportdaten" (`ship-transport`) - die sechs Termin-/Containerfelder, WOERTLICH
 * umgezogen aus `MainTab/Termine.tsx`: ETD (tatsächlich), ETA (erwartete Ankunft), vsl. LT 74722
 * Buchen, Aufschlag auf ETA (Tage), Container, Container-Volumen (m³). `Bestelldatum` und
 * „ETD (PI)" bleiben im Reiter „Bestellung" (`MainTab/Termine.tsx`) - siehe dessen Kopfkommentar,
 * „ETD (PI) bleibt bewusst dort".
 *
 * ================= Verhaltensaenderung 5 - hier NEU verankert (Auftrag Abschnitt 3) ============
 *
 * Bislang (`MainTab/Termine.tsx`, vor diesem Umzug) entschied eine Aufloesung gegen ein im Store
 * gehaltenes Sendungs-Array ueber die Sperre - ein Speicherfeld, das seither ersatzlos entfernt ist
 * (Auftrag „Sendungsabruf beim App-Start entfernen", 2026-08-22). Das traf in Produktion NIE zu, und
 * zwar aus ZWEI Gruenden nacheinander: BIS ZUM 22.08.2026 war das Array selbst leer - es kam
 * ausschliesslich aus einem Sammelabruf OHNE Suchbegriff, und der lief wegen
 * `SUCHE_MINDESTLAENGE = 2` immer in ein leeres `[]`. SEIT `fc0fc26` (22.08.2026) liefert derselbe
 * Sammelabruf bis zu 50 zuletzt bewegte Sendungen, das Array war also gefuellt - die Sperre blieb
 * trotzdem inert, jetzt aus einem anderen Grund: die Aufloesung traf auf `order.shipmentId`, ein
 * Feld, das weder `Order` fuehrt noch das Backend je ausliefert. Beide Zustaende sind mit diesem
 * Auftrag Geschichte, ausfuehrlich in
 * `docs/superpowers/decisions/2026-08-22-sendungen-app-start.md`.
 *
 * Fuer DIESEN Abschnitt aendert das nichts: er haengt seit dem Umzug an `sendung !== null` (Punkt 1
 * unten, die tatsaechlich verknuepfte Sendung), nicht am Store-Array.
 *
 * Neu, zwei Punkte:
 *
 * 1. Gesperrt wird, wenn `sendung !== null` - an der tatsaechlich ueber
 *    `GET /api/orders/:id/shipment` verknuepften Sendung, nicht mehr am Store-Array. Seit der
 *    See/Flug-Umschaltung (Task 7) gilt zusaetzlich `transportMode === "see"`: in den Modi "air"
 *    und "road" (Asana 1218176179911324) bleiben die Container-Felder ungesperrt angezeigt (bzw.
 *    entfallen ganz, siehe unten) - Luft- und Straßenfracht kennen keine DSV-Sperre.
 * 2. Die WERTE kommen aus der BESTELLUNG, nicht aus der Sendung: `sendungVerknuepfen`
 *    (`backend/src/dsv/verknuepfung.ts`) schreibt beim Verknuepfen `etd`/`eta`/`container_type`/
 *    `container_volume` in `order_extras` - die Bestellung fuehrt die DSV-Werte danach selbst. Ein
 *    zweiter Lesepfad ueber die Sendung waere eine zweite Wahrheit.
 *
 *    RICHTIGGESTELLT (Auftrag „Sendungs-Dropdown, Portal-Link, Konfliktdialog", 2026-08-21): hier
 *    stand, `SendungsTreffer` fuehre `etd`/`containerType`/`containerVolume` „ohnehin nicht" - das
 *    stimmt seit der Erweiterung dieses Auftrags (`lib/types.ts#SendungsTreffer`) nicht mehr fuer
 *    `etd` und `containerType`, beide sind jetzt Felder der Serverform. `containerVolume` gibt es
 *    dort weiterhin nicht namensgleich (die Sendung fuehrt `volumeM3`, eine andere Form/Einheit als
 *    das `order_extras`-Feld `container_volume`). Der eigentliche Grund fuer den Lesepfad ueber die
 *    BESTELLUNG bleibt aber Punkt 2 selbst (keine zweite Wahrheit) - NICHT die (nun ueberholte)
 *    Behauptung, die Sendung koenne die Werte gar nicht liefern.
 *
 * Unveraendert: die Kennzeichnung heisst weiter „DSV-geführt" (`DSV_KENNZEICHNUNG` unten - EINZIGE
 * Deklaration, `test/komponenten/Drawer/dsvGefuehrt.test.tsx` importiert sie von hier), der
 * Aufschlag auf ETA bleibt IN JEDEM FALL bedienbar, und „vsl. LT 74722 Buchen" wird bei
 * verknuepfter Sendung aus der ETA gerechnet (`estimatedBuchenDateFromEta(eta, aufschlag)`) statt
 * aus dem gespeicherten Wert.
 *
 * ================= WELCHES ETD? Zwei Felder, ein Name im Brief =================
 *
 * (Wiederhergestellt beim Umzug, C4 einer Pruefung, 2026-08-21 - stand woertlich in
 * `MainTab/Termine.tsx`, bevor die Flaeche hierher zog, und war seither nur noch ein toter Verweis
 * dort UND in `lib/orderListEditor.ts`, beide auf einen Abschnitt, den es hier nicht gab.)
 *
 * `index.html` fuehrt ZWEI ETD-Felder: `etdPi` („ETD (PI)", die Zusage aus der Proforma) und `etd`
 * („ETD (tatsächlich)"). Die Sendung fuehrt `etd`, also ist „ETD (tatsächlich)" das Feld, das bei
 * verknuepfter Sendung sperrt (`dsv` oben) - „ETD (PI)" bleibt bedienbar und steht seit dem Umzug im
 * Reiter „Bestellung" (`MainTab/Termine.tsx`), weil es eine Zusage des Lieferanten ist und keine
 * Transportmeldung. Der Task-Brief nennt nur „ETD"; siehe Task-Bericht.
 */
import { Datumsfeld } from "../Datumsfeld.tsx";
import { estimatedBuchenDateFromEta, etaBookingOffsetDays } from "../../../lib/format.ts";
import { CONTAINER_TYPES } from "../../../config/shipping.ts";
import { feldZustandClass } from "../../../utils/feldZustand.ts";
import type { Order, SendungsTreffer } from "../../../lib/types.ts";

/** Die Kennzeichnung an jedem DSV-gefuehrten Feld (Spec, Verhaltensaenderung 5). */
export const DSV_KENNZEICHNUNG = "DSV-geführt";

/**
 * Feldzustand-Optik (Referenz `feldzustand-spec.md`): Tooltip der vier Felder, die NUR gesperrt
 * sind, weil eine DSV-Sendung verknuepft ist (`dsv === true`) - ein dritter Sperrgrund, weder
 * rollenbasiert noch strukturell. Exakter Text aus der Spec, nicht selbst formuliert.
 */
const DSV_GESPERRT_TITEL = "Gesperrt, solange eine DSV-Sendung verknüpft ist.";

/** Feldzustand-Optik: „vsl. LT 74722 Buchen" ist strukturell gesperrt, UNABHAENGIG von `dsv`. */
const VSL_BUCHEN_TITEL = "Reines Anzeigefeld, kein Eingabefeld.";

function DsvHinweis() {
  return <span className="dsv-note">{DSV_KENNZEICHNUNG}</span>;
}

export interface TransportProps {
  bestellung: Order;
  /** `transportMode === "see" && sendung !== null` entscheidet ueber die Sperre - siehe Kopfkommentar. */
  sendung: SendungsTreffer | null;
  onAendern: (id: string, aenderung: Record<string, unknown>) => void;
}

export function Transport({ bestellung: o, sendung, onAendern }: TransportProps) {
  const id = o.id as string;
  const patch = (feld: string, wert: unknown): void => onAendern(id, { [feld]: wert });

  // Feature "See/Flug-Umschaltung" (Spec E4), um "road" erweitert (Asana 1218176179911324): die
  // Container-Sperre UND die Container-Felder selbst gelten nur im Modus "see" - Luft- und
  // Straßenfracht kennen keine Container.
  const transportMode = o.transportMode ?? "see";
  const dsv = transportMode === "see" && sendung !== null;

  // Neuverankerung Punkt 2 (Kopfkommentar): die Werte kommen aus der BESTELLUNG, unabhaengig
  // davon, ob eine Sendung verknuepft ist - `sendungVerknuepfen` hat sie dort bereits hinterlegt.
  const etd = (o.etd as string) ?? "";
  const eta = (o.eta as string) ?? "";
  const containerType = (o.containerType as string) ?? "";
  const containerVolume = (o.containerVolume as string) ?? "";

  // Siehe den Kommentar am `<select>` unten: der Bestand hat keinen leeren Eintrag, ein Wert ohne
  // Entsprechung stellt im Browser die ERSTE Groesse dar.
  const angezeigterContainer = CONTAINER_TYPES.some((c) => c.id === containerType)
    ? containerType
    : CONTAINER_TYPES[0].id;

  // Der Aufschlag bleibt IN JEDEM FALL bedienbar - und der abgeleitete Termin folgt der gesperrten
  // ETA. Ohne Sendung bleibt der gespeicherte Wert stehen, wie im Bestand.
  const aufschlag = etaBookingOffsetDays(o);
  const vslBuchen = dsv
    ? estimatedBuchenDateFromEta(eta, aufschlag)
    : ((o.estimatedDeliveryBuchen as string) || "");

  /** `patchEta` (index.html:9649) - `setOrderEta` zieht `estimatedDeliveryBuchen` mit. */
  const etaSetzen = (wert: string): void =>
    onAendern(id, {
      eta: wert || "",
      estimatedDeliveryBuchen: estimatedBuchenDateFromEta(wert || "", etaBookingOffsetDays(o)),
    });

  /** `patchEtaBookingOffset` (index.html:9659) - die ZAHL, nicht der Rohtext, plus der Termin. */
  const aufschlagSetzen = (wert: string): void => {
    const tage = etaBookingOffsetDays(wert);
    onAendern(id, {
      etaBookingOffsetDays: tage,
      estimatedDeliveryBuchen: estimatedBuchenDateFromEta(eta, tage),
    });
  };

  /** `setContainerType` (index.html:9673) - Nennvolumen vorbelegen, wenn noch keines gesetzt ist. */
  const containerTypSetzen = (wert: string): void => {
    const t = CONTAINER_TYPES.find((c) => c.id === wert);
    const aenderung: Record<string, unknown> = { containerType: wert };
    if (t && t.vol && !(Number(o.containerVolume) > 0)) aenderung.containerVolume = String(t.vol);
    onAendern(id, aenderung);
  };

  return (
    <>
      <div className="field">
        <label>ETD (tatsächlich)</label>
        {/* Feldzustand-Optik: `dsv` entscheidet dynamisch - gesperrt (dritter Grund, siehe
            `DSV_GESPERRT_TITEL`) wenn verknuepft, sonst erwartet/amber wenn leer. */}
        {/*
         * Auftrag 8 (strukturell statt Sibling-Element): kein `.feld-lock-anchor`-Span mehr - das
         * Schloss-Symbol kommt seither als CSS `background-image` direkt ueber `.feld-gesperrt`
         * (`styles/neubau/feldzustand.css`), gesetzt am `<input>`, das `Datumsfeld` selbst rendert.
         * `DsvHinweis` bleibt unveraendert stehen (kein Feld, eigener Hinweistext).
         */}
        <Datumsfeld
          wert={etd}
          disabled={dsv}
          className={feldZustandClass({ gesperrt: dsv, erwartet: true, leer: etd === "" })}
          title={dsv ? DSV_GESPERRT_TITEL : undefined}
          onCommit={(wert) => patch("etd", wert)}
        />
        {dsv ? <DsvHinweis /> : null}
      </div>
      <div className="field">
        <label>ETA (erwartete Ankunft)</label>
        <Datumsfeld
          wert={eta}
          disabled={dsv}
          className={feldZustandClass({ gesperrt: dsv, erwartet: true, leer: eta === "" })}
          title={dsv ? DSV_GESPERRT_TITEL : undefined}
          onCommit={(wert) => etaSetzen(wert)}
        />
        {dsv ? <DsvHinweis /> : null}
      </div>
      <div className="field">
        <label>vsl. LT 74722 Buchen</label>
        {/* Im Bestand ein reines Anzeigefeld (`readonly aria-readonly`), das `patchEta` und
            `patchEtaBookingOffset` von aussen nachfuehren (index.html:8459). Feldzustand-Optik:
            strukturell gesperrt, UNABHAENGIG von `dsv` - siehe `VSL_BUCHEN_TITEL`. */}
        {/* Auftrag 8: kein `.feld-lock-anchor`-Span mehr, siehe Kommentar am ETD-Feld oben. */}
        <input
          id="estimatedDeliveryBuchen"
          type="date"
          value={vslBuchen}
          readOnly
          aria-readonly="true"
          className={feldZustandClass({ gesperrt: true })}
          title={VSL_BUCHEN_TITEL}
        />
      </div>
      <div className="field">
        <label>Aufschlag auf ETA (Tage)</label>
        <input
          id="etaBookingOffsetDays"
          className="mono"
          type="number"
          min="0"
          max="365"
          step="1"
          value={aufschlag}
          onChange={(e) => aufschlagSetzen(e.currentTarget.value)}
        />
      </div>
      {transportMode === "see" ? (
        <>
          <div className="field">
            <label>Container</label>
            {/*
             * `index.html:8465-8469` kennt KEINEN leeren Eintrag: passt `o.containerType` auf keine
             * der beiden Groessen (etwa bei einer neu angelegten Bestellung), traegt kein `<option>`
             * das `selected` - und der Browser stellt dann die ERSTE Auswahl dar. React dagegen
             * setzte bei einem Wert ohne Entsprechung `selectedIndex = -1` und zeigte ein leeres Feld.
             * `angezeigterContainer` bildet deshalb nach, was der Browser mit dem Markup des Bestands
             * tut. Gespeichert wird dadurch nichts - `setContainerType` laeuft nur bei einer Auswahl.
             */}
            <select
              value={angezeigterContainer}
              disabled={dsv}
              className={feldZustandClass({ gesperrt: dsv })}
              title={dsv ? DSV_GESPERRT_TITEL : undefined}
              onChange={(e) => containerTypSetzen(e.currentTarget.value)}
            >
              {CONTAINER_TYPES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
            {dsv ? <DsvHinweis /> : null}
          </div>
          <div className="field">
            <label>Container-Volumen (m³)</label>
            <input
              className={`mono ${feldZustandClass({ gesperrt: dsv })}`}
              type="number"
              min="0"
              step="0.1"
              value={containerVolume}
              placeholder="z. B. 67"
              disabled={dsv}
              title={dsv ? DSV_GESPERRT_TITEL : undefined}
              onChange={(e) => patch("containerVolume", e.currentTarget.value)}
            />
            {dsv ? <DsvHinweis /> : null}
          </div>
        </>
      ) : null}
    </>
  );
}
